import { create as createProto } from '@bufbuild/protobuf';
import {
  WS_AUTH_CONFIRM_TIMEOUT_MS,
  WS_CONNECT_TIMEOUT_MS,
  WS_HARD_BLOCK_THRESHOLD_MS,
  WS_HEARTBEAT_INTERVAL_MS,
  WS_LIVENESS_PROBE_TIMEOUT_MS,
  WS_MESSAGE_QUEUE_MAX_SIZE,
  WS_PONG_TIMEOUT_MS,
  WS_RECONNECT_BASE_DELAY_MS,
  WS_RECONNECT_GIVE_UP_MS,
  WS_RECONNECT_JITTER,
  WS_RECONNECT_MAX_DELAY_MS,
  WS_RECONNECT_MULTIPLIER,
  WS_SOFT_BLOCK_GRACE_MS,
} from '@/lib/constants';
import { NetworkError, ProtocolError } from '@/lib/errors';
import type { WireError } from '@/lib/errors/catalog';
import { wireErrorFromEnvelope } from '@/lib/errors/fromEnvelope';
import { useFeatureFlagsStore } from '@/lib/featureFlags';
import { LIMIT_KEYS } from '@/lib/limits/keys';
import {
  createEnvelope,
  createPingEnvelope,
  deserializeEnvelope,
  serializeEnvelope,
} from '@/lib/protobuf/codec';
import { BeginAuthenticationRequestSchema } from '@/lib/protobuf/gen/auth/auth_pb';
import type { SessionMovedFrame, SessionRedirect } from '@/lib/protobuf/gen/cluster/cluster_pb';
import type { MessageEnvelope } from '@/lib/protobuf/gen/envelope/envelope_pb';
import { useAuthStore } from '@/stores/authStore';
import { useSessionGuardStore } from '@/stores/sessionGuardStore';
import { clearAffinity, getAffinityURL, storeAffinity } from './wsAffinity';

// Phase 4 (multi-backend user migration) close codes — see
// docs/plans/2026-05-18-multibackend-user-migration-design.md §4.
//   4001: "session reassigned" — sent after a SessionMovedFrame, the FE has
//         already cached the new target and will route the reconnect there.
//   4002: "lease lost" — the backend revoked our affinity; the cached URL
//         is stale and the FE must re-discover via the default URL.
const WS_CLOSE_LEASE_LOST = 4002;

export type ConnectionState = 'connected' | 'connecting' | 'disconnected' | 'error' | 'redirecting';

/**
 * What send() actually did with the bytes. ADDITIVE: send()/sendEnvelope()
 * returned `void` and 173 call sites across 42 files ignore the result — they
 * keep compiling and keep behaving identically. Only a caller that *reads* the
 * outcome (the chat outbox) sees any difference.
 *
 * There is deliberately no 'dropped': a full queue evicts the OLDEST entry,
 * which already returned 'queued' to its own caller long ago. That loss is
 * reported through listeners, not through this return value (LIMIT-37): a
 * tagged entry's owner hears it via onQueueEvicted, and anything no owner
 * claimed raises one onBackgroundDropped notice per outage.
 */
export type SendOutcome = 'sent' | 'queued';

export interface SendOpts {
  /**
   * Opaque caller-side id. Tagging an entry is what makes it withdrawable
   * (withdrawQueued) and what lets its owner hear about an eviction at the
   * cap (onQueueEvicted). An untagged eviction can only raise the
   * once-per-outage background notice (onBackgroundDropped).
   */
  correlationId?: string | undefined;
}

// NOTE: there is deliberately no per-entry TTL here, and no expiry callback.
// CHAT-12 shipped both, and CHAT-19 §1 established they had no live reader:
// chatStore's drain calls withdrawQueued() the moment send() reports 'queued',
// so an outbox entry is never in this queue when flushQueue runs. Other tagged
// entries (sendRequest) do sit here, and hear about an eviction through
// onQueueEvicted rather than a timer. A TTL that can
// never fire is worse than no TTL — it reads as a guarantee. If a future caller
// genuinely needs the transport to own a queued entry over time, add the TTL
// back TOGETHER with the caller that reads its expiry.
//
// What must NOT change: a queued entry survives an arbitrarily long outage.
// ENG-46's queued subscribes (fa230359b) and the 2026-08-15 reconnect-toast fix
// (849052d4d) both depend on it, and `flushes queued entries regardless of age`
// in the test file is the assertion that holds the line.

interface QueuedMessage {
  data: Uint8Array;
  enqueuedAt: number;
  // `| undefined` throughout: tsconfig sets exactOptionalPropertyTypes, so an
  // untagged send() (opts === undefined) would not otherwise be assignable.
  opts?: SendOpts | undefined;
}

export type MessageHandler = (envelope: MessageEnvelope) => void;

/**
 * ErrorHandler receives the LEGACY carrier's (code = HTTP status, message =
 * safe server string) for back-compat branching, plus the normalized typed
 * `wire` error (preferring the ErrorEvent carrier) so toast surfaces can
 * resolve the LOCALIZED catalog copy instead of echoing the English server
 * message (the "Serverfehler (500): Sorry — something went wrong…" mixed-
 * language toast).
 */
export type ErrorHandler = (code: number, message: string, wire?: WireError) => void;

type ConnectionStateCallback = (state: ConnectionState, attempt: number, delay: number) => void;

/**
 * Called when the offline queue evicts a TAGGED entry at the cap. Return true
 * when the listener owns `correlationId` (it has told its caller); an eviction
 * no listener claims counts as a background drop.
 */
export type QueueEvictedListener = (correlationId: string, cap: number) => boolean;
/** Called once per outage, after the state is back to 'connected', with the cap that evicted. */
export type BackgroundDroppedListener = (cap: number) => void;

export class WebSocketManager {
  private ws: WebSocket | null = null;
  private url: string = '';
  private handlers = new Map<string, Set<MessageHandler>>();
  private wildcardHandlers = new Set<MessageHandler>();
  private errorHandlers = new Set<ErrorHandler>();
  private messageQueue: QueuedMessage[] = [];
  private queueEvictedListeners = new Set<QueueEvictedListener>();
  private backgroundDroppedListeners = new Set<BackgroundDroppedListener>();
  // Set when an eviction no owner claimed; cleared by the one notice
  // emitBackgroundDropped sends after the next 'connected'.
  private droppedThisOutage = false;
  private droppedCap = 0;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  // Short-timeout probe fired on return-to-foreground to detect a zombie socket
  // (OPEN to us, already dead at the OS). Distinct from pongTimer (the 60s
  // heartbeat timeout) so the two never clobber each other's handle.
  private livenessTimer: ReturnType<typeof setTimeout> | null = null;
  private foregroundProbeInFlight = false;
  // Sockets created so far; see getConnectionEpoch.
  private connectionEpoch = 0;
  // Watchdog for a socket stuck in CONNECTING (never fires onopen/onclose —
  // the mobile tab-resume half-open trap). Armed in createConnection, cleared
  // the instant the socket opens or closes.
  private connectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private softBlockTimer: ReturnType<typeof setTimeout> | null = null;
  private hardBlockTimer: ReturnType<typeof setTimeout> | null = null;
  private giveUpTimer: ReturnType<typeof setTimeout> | null = null;
  // The soft/hard/give-up ladder measures ONE continuous stretch of downtime.
  // It must NOT reset on each reconnect *attempt* (or the hard overlay would be
  // pushed back — or with fast retries, never fire). startDisconnectTimers is
  // therefore idempotent: it arms once and no-ops until clearDisconnectTimers
  // (a successful open, or an explicit restart) disarms it.
  private disconnectTimersArmed = false;
  private reconnectAttempt = 0;
  private currentDelay = WS_RECONNECT_BASE_DELAY_MS;
  private connectionState: ConnectionState = 'disconnected';
  private shouldReconnect = false;
  private onConnectionStateChange: ConnectionStateCallback | null = null;
  private onConnectCallbacks = new Set<() => void>();

  // --- Authentication gate ---
  // The server rejects every non-auth message on a connection until it has
  // processed beginAuthenticationRequest. On a flaky link (frequent
  // reconnects) anything that raced ahead of auth — feature reconnect
  // callbacks, queued messages, page-mount sendEnvelope() calls — used to be
  // silently dropped. We now: (1) send the auth handshake first on every
  // open, (2) hold all other outbound traffic in the queue until the server
  // confirms auth (authenticationSuccessResponse), (3) only then flush.
  private authCallback: (() => Promise<void> | void) | null = null;
  private authenticated = false;
  private authConfirmedResolve: (() => void) | null = null;
  private authConfirmedPromise: Promise<void> | null = null;

  // Phase 4 affinity tracking. usedAffinityURL is set when createConnection
  // resolved its URL from getAffinityURL() rather than this.url; we use it
  // in onclose / onerror to detect a stale-cached-URL failure (open never
  // succeeded) and fall back to the default URL exactly once.
  private usedAffinityURL = false;
  private affinityOpened = false;

  // --- Foreground fast-path ---
  // Mobile browsers (esp. Firefox-Android) freeze the tab and the OS tears down
  // the idle socket on lock / app-switch. There is no event for "your socket
  // just died in the background"; without help we'd wait out the 30s heartbeat →
  // 60s pong cycle plus a grown exponential backoff before noticing on return.
  // These listeners collapse that to ~1s: on return-to-foreground we reconnect a
  // dead socket immediately (resetting backoff) or probe a seemingly-OPEN one.
  // Stored as stable bound refs so removeEventListener matches.
  private readonly onVisibilityChange = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      this.handleForeground();
    }
  };
  private readonly onOnline = (): void => this.handleForeground();
  private readonly onPageShow = (ev: Event): void => {
    // Only bfcache restores (persisted) imply a possibly-dead socket. A normal
    // initial-load pageshow is a no-op anyway (shouldReconnect is still false).
    if ((ev as PageTransitionEvent).persisted) this.handleForeground();
  };

  private installLifecycleListeners(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onOnline);
      window.addEventListener('pageshow', this.onPageShow);
    }
  }

  private removeLifecycleListeners(): void {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onOnline);
      window.removeEventListener('pageshow', this.onPageShow);
    }
  }

  // handleForeground is the single entry-point for all "the user just came back"
  // signals. It delegates to probeLiveness, which either probes a live-looking
  // socket or forces an immediate reconnect of a dead one — never reconnects a
  // genuinely-healthy socket.
  private handleForeground(): void {
    this.probeLiveness('foreground');
  }

  /**
   * Increments once per socket this manager creates. A request sent on an older
   * epoch went out on a socket that is gone (note-save resend ownership, NOTES-15 D8).
   */
  getConnectionEpoch(): number {
    return this.connectionEpoch;
  }

  /**
   * Verify the socket is alive now: a live-looking OPEN socket gets a liveness ping and is force-closed
   * after WS_LIVENESS_PROBE_TIMEOUT_MS without a pong; a dead socket reconnects immediately, skipping backoff.
   * No-op after an intentional disconnect, while a probe is already deciding, or while a socket is CONNECTING.
   * Public for callers that have their own evidence of a zombie socket (a note save with no reply).
   */
  probeLiveness(reason: string): void {
    if (!this.shouldReconnect) return; // intentional disconnect — leave it closed
    if (this.foregroundProbeInFlight) return; // a probe is already deciding

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Socket *claims* alive — verify with a short liveness ping. A pong (or a
      // racing heartbeat pong) clears both timers; no pong ⇒ force-close, which
      // routes through onclose → scheduleReconnect.
      this.foregroundProbeInFlight = true;
      this.sendNow(serializeEnvelope(createPingEnvelope()));
      if (this.livenessTimer) clearTimeout(this.livenessTimer);
      this.livenessTimer = setTimeout(() => {
        this.livenessTimer = null;
        this.foregroundProbeInFlight = false;
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          console.warn('[ws] liveness probe got no pong; closing socket', { reason });
          this.ws.close(); // zombie socket — drop it and reconnect
        }
      }, WS_LIVENESS_PROBE_TIMEOUT_MS);
    } else {
      // A reconnect already in flight (CONNECTING) will resolve via onopen or
      // onclose — don't stack a second socket. Two foreground signals can fire
      // in the same tick on mobile unlock (visibilitychange + online), and the
      // orphaned socket's onclose would otherwise schedule a spurious reconnect.
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) return;
      // Socket is gone — reconnect now, skip the backoff.
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      // Clear the soft/hard/give-up escalation timers so a fast reconnect on a
      // slow link doesn't flash the session-guard "reconnecting" banner; onopen
      // also clears them, but the 10s soft-block could otherwise fire first.
      this.clearDisconnectTimers();
      this.reconnectAttempt = 0;
      this.currentDelay = WS_RECONNECT_BASE_DELAY_MS;
      this.createConnection();
    }
  }

  connect(url: string): void {
    this.url = url;
    this.shouldReconnect = true;
    this.reconnectAttempt = 0;
    this.currentDelay = WS_RECONNECT_BASE_DELAY_MS;
    this.installLifecycleListeners();
    this.createConnection();
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.removeLifecycleListeners();
    this.clearDisconnectTimers();
    this.cleanup();
    // A drop flagged in this session must not surface in the next one (logout,
    // account switch); the notice belongs to the outage that caused it.
    this.droppedThisOutage = false;
    this.droppedCap = 0;
    this.setConnectionState('disconnected');
  }

  send(data: Uint8Array, opts?: SendOpts): SendOutcome {
    // Hold everything until the socket is open AND the server has confirmed
    // authentication — otherwise the server rejects it. Queued traffic is
    // flushed (in order) once auth lands.
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.authenticated) {
      this.ws.send(data);
      return 'sent';
    }
    // LIMIT-37: the cap is the reported `ws.offline_queue` limit, and `while`
    // (not `if`) so a cap lowered mid-outage evicts down to it. Every eviction
    // is reported (reportEviction): a sendRequest owner rejects its promise at
    // once; anything unclaimed raises one background notice per outage. Chat's
    // outbox withdraws its own entries immediately, so it never reaches here.
    const cap = this.queueMax();
    while (this.messageQueue.length >= cap) {
      const evicted = this.messageQueue.shift();
      if (evicted) this.reportEviction(evicted, cap);
    }
    this.messageQueue.push({ data, enqueuedAt: Date.now(), opts });
    return 'queued';
  }

  // The reported `ws.offline_queue` limit; the constant is the named fallback
  // until /api/v1/features has loaded. parseLimits already drops max <= 0.
  private queueMax(): number {
    return useFeatureFlagsStore.getState().limits[LIMIT_KEYS.wsOfflineQueue]?.max ?? WS_MESSAGE_QUEUE_MAX_SIZE;
  }

  private reportEviction(entry: QueuedMessage, cap: number): void {
    const id = entry.opts?.correlationId;
    let claimed = false;
    if (id !== undefined) {
      for (const listener of this.queueEvictedListeners) {
        try {
          if (listener(id, cap)) claimed = true;
        } catch (err) {
          console.error('Queue eviction listener error:', err);
        }
      }
    }
    if (!claimed) {
      this.droppedThisOutage = true;
      this.droppedCap = cap;
    }
  }

  // Called once, right after setConnectionState('connected') — never from
  // flushQueue, which on the auth path runs before the state is 'connected'.
  private emitBackgroundDropped(): void {
    if (!this.droppedThisOutage) return;
    this.droppedThisOutage = false;
    const cap = this.droppedCap;
    for (const listener of this.backgroundDroppedListeners) {
      try {
        listener(cap);
      } catch (err) {
        console.error('Background drop listener error:', err);
      }
    }
  }

  /** Subscribe to tagged evictions at the offline queue cap. Returns an unsub. */
  onQueueEvicted(listener: QueueEvictedListener): () => void {
    this.queueEvictedListeners.add(listener);
    return () => {
      this.queueEvictedListeners.delete(listener);
    };
  }

  /** Subscribe to the once-per-outage notice that unclaimed queued traffic was dropped. Returns an unsub. */
  onBackgroundDropped(listener: BackgroundDroppedListener): () => void {
    this.backgroundDroppedListeners.add(listener);
    return () => {
      this.backgroundDroppedListeners.delete(listener);
    };
  }

  /**
   * Remove a queued entry by correlationId; true if one was actually removed.
   * For a caller that owns its own retry (the chat outbox): withdraw first,
   * then re-dispatch, so the transport's flush and the caller's drain cannot
   * both send the same message.
   */
  withdrawQueued(correlationId: string): boolean {
    const index = this.messageQueue.findIndex((m) => m.opts?.correlationId === correlationId);
    if (index === -1) return false;
    this.messageQueue.splice(index, 1);
    return true;
  }


  // sendNow bypasses the auth/queue gate — used only for the auth handshake
  // itself (which obviously can't wait for auth) and for heartbeat pings.
  private sendNow(data: Uint8Array): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    }
  }

  // NO session-guard check here — deliberately, and this is the load-bearing part.
  //
  // sendEnvelope used to toast "Reconnecting — try again in a moment" and DROP the
  // message whenever blockState was 'hard'. The rationale was "refuse user sends
  // while disconnected, and say why". That rationale does not survive contact with
  // what 'hard' actually renders: DisconnectOverlay is a MODAL `fixed inset-0 z-50`
  // dialog with no dismiss control, shown for exactly blockState === 'hard'. While
  // the guard is hard the user cannot click anything, so a user-initiated send in
  // that state is UNREACHABLE — every call that reached the branch was, by
  // construction, background work the user never asked for, and the toast stacked
  // one warning per such call on top of an overlay already saying "Reconnecting to
  // server…".
  //
  // That is why the same three toasts were reported twice on Android app-switch:
  //   2026-08-14 — the connect-callbacks ran while still 'hard' (fixed by moving
  //                the guard reset ahead of them in onopen).
  //   2026-08-15 — the reset only happens after the auth handshake resolves (up to
  //                WS_AUTH_CONFIRM_TIMEOUT_MS, longer with a crypto-unlock round
  //                trip), so the tab-resume burst — researchStore's visibilitychange
  //                refresh, RecordingUpload's, any queued effect — still landed
  //                inside a 'hard' window, on a freshly reloaded build.
  // Gating on the socket instead of the timer fixes only the second, and only when
  // the socket happens to still be OPEN; a socket Android already killed is back to
  // toasting. The state itself is the wrong thing to branch on.
  //
  // So: always fall through to send(), which is what every other send path does. It
  // queues while `!authenticated` (bounded by the `ws.offline_queue` limit, oldest
  // dropped and reported) and flushQueue() drains it once auth lands. Net effect: background
  // refreshes survive a reconnect instead of being discarded, and the user is told
  // about the outage once, by the overlay, instead of N times by toasts.
  sendEnvelope(envelope: MessageEnvelope, opts?: SendOpts): SendOutcome {
    return this.send(serializeEnvelope(envelope), opts);
  }

  // sendDuringAuth sends a message that is part of *completing* authentication
  // (e.g. the E2E-crypto unlock response): bypasses both the auth/queue gate
  // and the session-guard hard-block check, exactly like authenticate().
  //
  // Why this exists: for encrypted accounts the server sequence is
  //   FE → beginAuthenticationRequest → server → cryptoUnlockChallenge
  //      → FE → cryptoUnlockResponse → server → authenticationSuccessResponse
  // The gate only opens on authenticationSuccessResponse, so routing the
  // cryptoUnlockResponse through send()/sendEnvelope() would queue it forever
  // and deadlock the handshake ("Verifying encryption…" hangs). It must go out
  // immediately on the open socket.
  sendDuringAuth(envelope: MessageEnvelope): void {
    this.sendNow(serializeEnvelope(envelope));
  }

  onMessage(payloadCase: string, handler: MessageHandler): () => void {
    let handlerSet = this.handlers.get(payloadCase);
    if (!handlerSet) {
      handlerSet = new Set();
      this.handlers.set(payloadCase, handlerSet);
    }
    handlerSet.add(handler);

    return () => {
      const handlerSet = this.handlers.get(payloadCase);
      if (handlerSet) {
        handlerSet.delete(handler);
        if (handlerSet.size === 0) {
          this.handlers.delete(payloadCase);
        }
      }
    };
  }

  onAnyMessage(handler: MessageHandler): () => void {
    this.wildcardHandlers.add(handler);
    return () => {
      this.wildcardHandlers.delete(handler);
    };
  }

  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => {
      this.errorHandlers.delete(handler);
    };
  }

  setConnectionStateCallback(callback: ConnectionStateCallback): void {
    this.onConnectionStateChange = callback;
  }

  addConnectCallback(callback: () => void): () => void {
    this.onConnectCallbacks.add(callback);
    return () => {
      this.onConnectCallbacks.delete(callback);
    };
  }

  // setAuthCallback registers the single function the manager runs *first* on
  // every socket open (initial connect and every reconnect) — before any
  // feature connect-callback fires and before the queue is flushed. It must
  // send the auth handshake (typically via authenticate()). Returns an unsub.
  setAuthCallback(callback: () => Promise<void> | void): () => void {
    this.authCallback = callback;
    return () => {
      if (this.authCallback === callback) this.authCallback = null;
    };
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  authenticate(accessToken: string, deviceId?: string): void {
    const req = createProto(BeginAuthenticationRequestSchema, {
      token: accessToken,
      ...(deviceId && { deviceId }),
    });
    // Bypass the queue/auth gate — this *is* the auth message — and the
    // session-guard hard-block check, since auth is exactly how we recover
    // from a hard block.
    this.sendNow(
      serializeEnvelope(createEnvelope({ case: 'beginAuthenticationRequest', value: req })),
    );
  }

  private createConnection(): void {
    this.setConnectionState('connecting');

    // Fresh auth gate for this socket: nothing but the handshake (and pings)
    // goes out until the server confirms.
    this.authenticated = false;
    this.authConfirmedPromise = new Promise<void>((resolve) => {
      this.authConfirmedResolve = resolve;
    });

    // Prefer the cached lease-owner target if Phase 4 redirect has pinned
    // us to a specific backend. On first connect of a session this is null
    // and we use the default URL; the server may then SessionMovedFrame us
    // and the cache will be populated for the reconnect.
    const cachedTarget = getAffinityURL();
    const connectURL = cachedTarget ?? this.url;
    this.usedAffinityURL = cachedTarget !== null;
    this.affinityOpened = false;

    try {
      this.ws = new WebSocket(connectURL);
      this.connectionEpoch += 1;
      this.ws.binaryType = 'arraybuffer';

      // Arm the CONNECTING watchdog. If neither onopen nor onclose fires within
      // WS_CONNECT_TIMEOUT_MS the socket is wedged half-open — force-recycle it
      // so the reconnect ladder (and the hard-block overlay) can't stall forever.
      this.armConnectTimeout();

      this.ws.onopen = async () => {
        this.clearConnectTimeout();
        const thisWs = this.ws;
        if (!thisWs) return;
        this.affinityOpened = true;
        this.reconnectAttempt = 0;
        this.currentDelay = WS_RECONNECT_BASE_DELAY_MS;
        this.startHeartbeat();

        // 1) Authenticate FIRST. Until the server has processed the handshake
        //    it rejects every other message, so this must precede feature
        //    connect-callbacks, the queue flush, and any 'connected' re-render.
        if (this.authCallback) {
          try {
            await this.authCallback(); // sends beginAuthenticationRequest
          } catch (err) {
            console.error('WS auth callback failed:', err);
            void useAuthStore.getState().actions.forceLogout('reconnect_failed');
            return; // do not advertise 'connected'
          }
          // Wait for the server to confirm auth before letting anything else
          // out. Best-effort: an auth error (→ enrollment/logout, handled by
          // the app's onError handler) or a dead socket means we never
          // resolve — fall through after the timeout rather than hang.
          await Promise.race([
            this.authConfirmedPromise ?? Promise.resolve(),
            new Promise<void>((r) => setTimeout(r, WS_AUTH_CONFIRM_TIMEOUT_MS)),
          ]);
        } else {
          // No auth flow wired (e.g. tests) — nothing to gate on.
          this.authenticated = true;
        }

        // The socket may have died (or been replaced) while we awaited auth.
        if (this.ws !== thisWs || thisWs.readyState !== WebSocket.OPEN) return;

        // 2) Reconnect succeeded — clear escalation and the guard NOW, BEFORE any
        //    feature callback runs.
        //
        //    The ordering is load-bearing, not cosmetic. The guard's job is to block
        //    *user* sends while we are disconnected; once auth is confirmed on a live
        //    socket we are, by definition, connected. Resetting it after the callbacks
        //    (as this did until 2026-08-14) left blockState 'hard' for exactly the
        //    window in which every feature re-requests its state — and sendEnvelope
        //    DROPS a message under a hard block rather than queueing it.
        //
        //    Android app-switch was the reproducer: backgrounding for >30s trips
        //    WS_HARD_BLOCK_THRESHOLD_MS, so the guard is already 'hard' when the socket
        //    returns. Every restore request (chat history, research subscriptions,
        //    notes, tasks, hitl) was toasted "Reconnecting — try again in a moment"
        //    and silently discarded; the guard then reset, so the app advertised
        //    `connected` while holding no data, and chat history sat 'loading' until
        //    its own timer flipped it to 'error'. Only a page reload recovered.
        //    Desktop never reproduced it: a blip reconnects inside 30s, so the guard
        //    never reaches 'hard' and this window does not exist.
        this.clearDisconnectTimers();
        useSessionGuardStore.getState().actions.reset();

        // 3) Feature connect-callbacks (re-subscribe, reload chat history, …).
        //    A throw here is the feature's problem, not an auth failure — log
        //    and continue rather than logging the user out.
        for (const callback of this.onConnectCallbacks) {
          try {
            await callback();
          } catch (err) {
            console.error('WS connect callback failed:', err);
          }
        }

        if (this.ws !== thisWs || thisWs.readyState !== WebSocket.OPEN) return;

        this.setConnectionState('connected');
        this.emitBackgroundDropped();
        this.flushQueue();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        this.handleMessage(event);
      };

      this.ws.onclose = (ev: CloseEvent) => {
        this.clearConnectTimeout();
        this.authenticated = false;
        this.stopHeartbeat();

        // Phase 4: server-initiated "lease lost" — the cached target is
        // stale, drop it so the reconnect goes to the default URL and
        // re-discovers the owner via SessionMovedFrame.
        if (ev.code === WS_CLOSE_LEASE_LOST) {
          clearAffinity();
        } else if (this.usedAffinityURL && !this.affinityOpened) {
          // Connection to a cached affinity URL closed before it ever
          // opened — the backend is gone (took its `?b=<id>` route with it)
          // or otherwise refused us. Clear the cache and let the normal
          // reconnect path fall through to the default URL.
          clearAffinity();
        }

        if (this.shouldReconnect) {
          this.startDisconnectTimers();
          this.scheduleReconnect();
        } else {
          this.setConnectionState('disconnected');
        }
      };

      this.ws.onerror = () => {
        this.setConnectionState('error');
      };
    } catch {
      this.setConnectionState('error');
      if (this.shouldReconnect) {
        this.scheduleReconnect();
      }
    }
  }

  private handleMessage(event: MessageEvent): void {
    if (!(event.data instanceof ArrayBuffer)) {
      return;
    }

    let envelope: MessageEnvelope;
    try {
      envelope = deserializeEnvelope(new Uint8Array(event.data));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown deserialization error';
      console.error(new ProtocolError(`Failed to deserialize message: ${message}`));
      return;
    }

    // Handle error envelopes. Fires for any envelope with an error field set,
    // regardless of whether a payload is also present.
    if (envelope.error && envelope.error.message) {
      // Normalize once — prefers the typed ErrorEvent carrier when present, so
      // handlers can resolve localized catalog copy by ErrorCode.
      const wire = wireErrorFromEnvelope(envelope) ?? undefined;
      for (const handler of this.errorHandlers) {
        try {
          handler(envelope.error.code, envelope.error.message, wire);
        } catch (err) {
          console.error('Error handler error:', err);
        }
      }
    }

    const payloadCase = envelope.payload.case;

    // Auth confirmed — open the gate and release whatever queued behind it.
    // Fall through so the app's authenticationSuccessResponse handler (role
    // update, post-auth data fetches) still runs.
    if (payloadCase === 'authenticationSuccessResponse') {
      this.authenticated = true;
      this.authConfirmedResolve?.();
      this.flushQueue();
    }

    // Handle pong — reset both the heartbeat pong timeout and any in-flight
    // foreground liveness probe (a single pong proves the socket is alive).
    if (payloadCase === 'pong') {
      this.resetPongTimeout();
      if (this.livenessTimer) {
        clearTimeout(this.livenessTimer);
        this.livenessTimer = null;
      }
      this.foregroundProbeInFlight = false;
      return;
    }

    // Handle session redirect (Traefik cookie-based, pre-Phase-4 path).
    if (payloadCase === 'sessionRedirect') {
      const redirect = envelope.payload.value as SessionRedirect;
      this.handleRedirect(redirect);
      return;
    }

    // Phase 4: explicit lease-owner redirect with URL caching. Distinct from
    // sessionRedirect — that one re-uses the same URL (Traefik cookie picks
    // the route); this one carries a `?b=<id>` target that pins to the
    // lease-owning backend, and we cache it so subsequent reconnects skip
    // the redirect round-trip.
    if (payloadCase === 'sessionMovedFrame') {
      const frame = envelope.payload.value as SessionMovedFrame;
      this.handleSessionMoved(frame);
      return;
    }

    // Handle session revoked — server signals immediate forced logout
    if (payloadCase === 'sessionRevokedEvent') {
      void useAuthStore.getState().actions.forceLogout('session_revoked');
      return;
    }

    // LIMIT-05: a plan change can change plan-dependent limits, so refetch the
    // features payload too. NO return: budgetStore, subscriptionStore and the
    // dashboard layout handle this same event below.
    if (payloadCase === 'tierChangedEvent') {
      void useFeatureFlagsStore.getState().actions.refresh();
    }

    // Admin flipped a catalog feature flag — refetch the snapshot so every
    // useFeature(id) consumer re-renders with the new state. Silent UX:
    // no toast, no force-unmount; in-flight actions finish normally.
    if (payloadCase === 'featuresUpdatedEvent') {
      void useFeatureFlagsStore.getState().actions.refresh();
      return;
    }

    // Dispatch to wildcard handlers
    for (const handler of this.wildcardHandlers) {
      try {
        handler(envelope);
      } catch (err) {
        console.error('Wildcard message handler error:', err);
      }
    }

    // Dispatch to case-specific handlers
    if (payloadCase) {
      const handlerSet = this.handlers.get(payloadCase);
      if (handlerSet) {
        for (const handler of handlerSet) {
          try {
            handler(envelope);
          } catch (err) {
            console.error(`Message handler error for ${payloadCase}:`, err);
          }
        }
      }
    }
  }

  private handleRedirect(redirect: SessionRedirect): void {
    const { retryAfterMs } = redirect;

    // Close current connection cleanly
    this.shouldReconnect = true;
    this.cleanup();

    // Reset reconnect state for clean start
    this.reconnectAttempt = 0;
    this.currentDelay = retryAfterMs || 500;

    // Schedule reconnect — same URL, Traefik cookie will route correctly
    this.reconnectTimer = setTimeout(() => {
      this.createConnection();
    }, this.currentDelay);

    this.setConnectionState('redirecting');
  }

  private handleSessionMoved(frame: SessionMovedFrame): void {
    if (!frame.target) {
      // Empty target = backend has no peer to redirect us to (e.g. "no
      // capacity available" rejection). Drop any stale affinity and let
      // the normal reconnect-backoff handle it on the default URL.
      clearAffinity();
      console.warn(
        `WS session_moved rejected: no target (reason: ${frame.reason || 'unspecified'})`,
      );
      return;
    }
    storeAffinity(frame.target);
    // The server closes the WS immediately after this frame (code 4001);
    // the existing onclose path schedules a reconnect, which createConnection
    // will route to the cached target via getAffinityURL().
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const pingEnvelope = createPingEnvelope();
        this.ws.send(serializeEnvelope(pingEnvelope));
        this.startPongTimeout();
      }
    }, WS_HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
    // A closing/closed socket can't answer a foreground liveness probe — drop it
    // so a stale timer can't force-close the next socket.
    if (this.livenessTimer) {
      clearTimeout(this.livenessTimer);
      this.livenessTimer = null;
    }
    this.foregroundProbeInFlight = false;
  }

  private startPongTimeout(): void {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
    }
    this.pongTimer = setTimeout(() => {
      console.warn(new NetworkError('Pong timeout — closing connection'));
      this.ws?.close();
    }, WS_PONG_TIMEOUT_MS);
  }

  private resetPongTimeout(): void {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  private flushQueue(): void {
    if (!this.authenticated) return; // gate stays closed until auth lands
    while (this.messageQueue.length > 0) {
      const entry = this.messageQueue.shift();
      if (entry && this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(entry.data);
      } else {
        // Socket went away mid-flush — re-queue and stop.
        if (entry) this.messageQueue.unshift(entry);
        return;
      }
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempt++;

    // Exponential backoff with jitter
    const jitter = 1 + (Math.random() * 2 - 1) * WS_RECONNECT_JITTER;
    this.currentDelay = Math.min(
      this.currentDelay * WS_RECONNECT_MULTIPLIER * jitter,
      WS_RECONNECT_MAX_DELAY_MS,
    );

    this.setConnectionState('connecting');

    this.reconnectTimer = setTimeout(() => {
      this.createConnection();
    }, this.currentDelay);
  }

  // armConnectTimeout starts the CONNECTING watchdog for the socket just
  // created. onopen and onclose both clear it; if it fires first, the socket
  // never left CONNECTING (the mobile tab-resume half-open trap) and we
  // force-recycle it.
  private armConnectTimeout(): void {
    this.clearConnectTimeout();
    this.connectTimer = setTimeout(() => this.handleConnectTimeout(), WS_CONNECT_TIMEOUT_MS);
  }

  private clearConnectTimeout(): void {
    if (this.connectTimer) {
      clearTimeout(this.connectTimer);
      this.connectTimer = null;
    }
  }

  // handleConnectTimeout recovers from a socket wedged in CONNECTING. The
  // browser fired neither onopen nor onclose, so we can't trust close() to
  // fire onclose either — we drive the reconnect ourselves. We detach the
  // stuck socket's handlers so a late, out-of-order onclose can't
  // double-schedule, mirror onclose's stale-affinity handling, then schedule
  // the next attempt (keeping the soft/hard/give-up escalation armed).
  private handleConnectTimeout(): void {
    this.connectTimer = null;
    const stuck = this.ws;
    if (!stuck || stuck.readyState !== WebSocket.CONNECTING) return;

    console.warn(new NetworkError('WS connect timeout — socket stuck in CONNECTING, recycling'));

    stuck.onopen = null;
    stuck.onmessage = null;
    stuck.onclose = null;
    stuck.onerror = null;
    try {
      stuck.close();
    } catch {
      // A wedged socket may throw on close — best-effort, we've already
      // detached it and null it below.
    }
    this.ws = null;
    this.authenticated = false;
    this.stopHeartbeat();

    // A cached affinity URL that never opened is stale — drop it so the retry
    // falls back to the default URL (mirrors the onclose stale-cache path).
    if (this.usedAffinityURL && !this.affinityOpened) {
      clearAffinity();
    }

    if (this.shouldReconnect) {
      this.startDisconnectTimers();
      this.scheduleReconnect();
    } else {
      this.setConnectionState('disconnected');
    }
  }

  private cleanup(): void {
    this.authenticated = false;
    this.clearConnectTimeout();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearDisconnectTimers();
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
  }

  private startDisconnectTimers(): void {
    // Idempotent: already ticking against this downtime stretch — leave the
    // clock alone. (clearDisconnectTimers, on a successful open or an explicit
    // restart, is what re-opens the window.)
    if (this.disconnectTimersArmed) return;
    this.disconnectTimersArmed = true;
    this.softBlockTimer = setTimeout(
      () => useSessionGuardStore.getState().actions.escalate('soft'),
      WS_SOFT_BLOCK_GRACE_MS,
    );
    this.hardBlockTimer = setTimeout(
      () => useSessionGuardStore.getState().actions.escalate('hard'),
      WS_HARD_BLOCK_THRESHOLD_MS,
    );
    this.giveUpTimer = setTimeout(
      // Before wiping credentials, try to recover. The usual "give up" cause is a
      // stale access token (server reachable) or a transient outage — not a revoked
      // session. recoverOrLogout refreshes + reconnects, and only hard-logs-out on a
      // genuine 401. This stops a network blip from forcing a re-login (design doc §10).
      () =>
        void useAuthStore.getState().actions.recoverOrLogout(() => {
          this.reconnectAttempt = 0;
          this.currentDelay = WS_RECONNECT_BASE_DELAY_MS;
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          // Explicit restart: this is a *fresh* recovery cycle, so re-open the
          // escalation window (clear-then-arm past the idempotency guard) — if
          // this attempt also fails we want another give-up in 5 min.
          this.clearDisconnectTimers();
          this.startDisconnectTimers();
          this.createConnection();
        }),
      WS_RECONNECT_GIVE_UP_MS,
    );
  }

  private clearDisconnectTimers(): void {
    this.disconnectTimersArmed = false;
    if (this.softBlockTimer) {
      clearTimeout(this.softBlockTimer);
      this.softBlockTimer = null;
    }
    if (this.hardBlockTimer) {
      clearTimeout(this.hardBlockTimer);
      this.hardBlockTimer = null;
    }
    if (this.giveUpTimer) {
      clearTimeout(this.giveUpTimer);
      this.giveUpTimer = null;
    }
  }

  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.onConnectionStateChange?.(state, this.reconnectAttempt, this.currentDelay);
  }
}

export const wsManager = new WebSocketManager();
